import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import {
  AdditiveBlending,
  CanvasTexture,
  Color,
  NormalBlending,
  SRGBColorSpace,
  type Sprite,
  type Texture,
} from 'three';
import { prefersReducedMotion } from '../styles/tokens.js';

/**
 * What neglect looks like from the air: a wisp, then smoke, then a building
 * well alight.
 *
 * Health is a colour and a colour is easy to stop seeing. Fire is not — it
 * moves, it glows, it throws a column of smoke far above the roofline, and it
 * has to be seen from orbit or it is not doing its job. So nothing here is
 * sized against the building alone: every size is measured against whichever
 * is larger, the building or what the camera can make out from where it
 * stands. Pull back and a fire stays a fire rather than shrinking to a pixel.
 *
 * Sprites and sine waves, so there is no particle library to pull in and
 * nothing to ship.
 */
export interface NeglectProps {
  /** 0 = tended, 1 = long abandoned. */
  weathering: number;
  /** The building's footprint. */
  width: number;
  /** Height of the roof the smoke leaves from. */
  roof: number;
  texture: Texture | null;
}

/** Below this nothing shows: a day or two late is not a fire. */
const STARTS = 0.12;
/** Past this the building is alight rather than merely smoking. Matches the API. */
const BURNS = 0.8;
/** Embers start glowing in the smoke before it catches. */
const GLOWS = 0.5;

/**
 * How big a thing must be, per unit of camera distance, to read from there.
 * At the globe's farthest a burning building is drawn about a tenth of the
 * planet's radius across, which is a fire you notice.
 */
const READABLE = 0.02;

const SMOKE_PALE = new Color('#c4c8cf');
// Dark, but not black: black smoke disappears over the ocean and into space.
const SMOKE_DARK = new Color('#4a4d54');
// Where the fire lights the underside of its own smoke.
const SMOKE_LIT = new Color('#c8662e');
const FLAME_DEEP = new Color('#d8321c');
const FLAME_MID = new Color('#ff7a1a');
const FLAME_CORE = new Color('#ffe08a');
const EMBER = new Color('#ff9a3c');

const FLAME_TONGUES = 5;
const EMBERS = 6;

let flameCache: Texture | null | undefined;

/** A soft teardrop, so a flame has a tip rather than being another blob. */
export function flameTexture(): Texture | null {
  if (flameCache !== undefined) return flameCache;
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 128;
  const ctx = canvas.getContext('2d');
  if (!ctx) return (flameCache = null);
  const glow = ctx.createRadialGradient(32, 92, 0, 32, 92, 34);
  glow.addColorStop(0, 'rgba(255,255,255,1)');
  glow.addColorStop(0.5, 'rgba(255,255,255,0.55)');
  glow.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.moveTo(32, 2);
  ctx.bezierCurveTo(44, 40, 64, 64, 60, 96);
  ctx.bezierCurveTo(56, 124, 8, 124, 4, 96);
  ctx.bezierCurveTo(0, 64, 20, 40, 32, 2);
  ctx.fill();
  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  return (flameCache = texture);
}

/** Deterministic jitter, so every device draws the same fire. */
function jitter(i: number, salt: number): number {
  const x = Math.sin(i * 12.9898 + salt * 78.233) * 43758.5453;
  return x - Math.floor(x);
}

export function Neglect({ weathering, width, roof, texture }: NeglectProps) {
  const still = prefersReducedMotion();
  const { invalidate, camera } = useThree();
  const puffs = useRef<Sprite[]>([]);
  const flames = useRef<Sprite[]>([]);
  const embers = useRef<Sprite[]>([]);
  const halo = useRef<Sprite>(null);
  const flame = useMemo(() => flameTexture(), []);

  // How many puffs, how dark, how tall — all one dial.
  const plume = useMemo(() => {
    if (!texture || weathering < STARTS) return null;
    const heat = Math.min(1, (weathering - STARTS) / (1 - STARTS));
    return {
      heat,
      count: 3 + Math.round(heat * 7),
      colour: SMOKE_PALE.clone().lerp(SMOKE_DARK, Math.min(1, heat * 1.3)),
      rise: 4 + heat * 9,
      spread: 0.5 + heat * 1.1,
      speed: 0.18 + heat * 0.2,
      opacity: 0.4 + heat * 0.5,
      glowing: weathering >= GLOWS,
      burning: weathering >= BURNS,
    };
  }, [weathering, texture]);

  // A burning building keeps the renderer awake; it is meant to.
  useEffect(() => {
    if (plume) invalidate();
  }, [plume, invalidate]);

  useFrame((state) => {
    if (!plume) return;
    const time = still ? 0 : state.clock.elapsedTime;
    // The unit everything is measured in: the building, or what can be seen
    // from here, whichever is bigger.
    const unit = Math.max(width, camera.position.length() * READABLE);

    for (const [i, puff] of puffs.current.entries()) {
      if (!puff) continue;
      // Each puff walks the same path, staggered, so a handful of sprites read
      // as one continuous column of smoke leaning off with the wind.
      const phase = still ? (i + 0.5) / plume.count : (time * plume.speed + i / plume.count) % 1;
      const lean = phase * phase * unit * plume.spread * 1.6;
      puff.position.y = roof + phase * plume.rise * unit;
      puff.position.x = lean + Math.sin(phase * 5 + i) * unit * plume.spread * 0.35 * phase;
      puff.position.z = Math.cos(phase * 3.7 + i * 2) * unit * plume.spread * 0.35 * phase;
      const size = unit * (1 + phase * (2.5 + plume.heat * 3));
      puff.scale.set(size, size, 1);
      const material = puff.material as { opacity: number; color: Color };
      material.opacity = plume.opacity * Math.min(1, phase * 5) * (1 - phase) ** 0.7;
      if (plume.burning) material.color.copy(SMOKE_LIT).lerp(plume.colour, Math.min(1, phase * 3));
    }

    for (const [i, tongue] of flames.current.entries()) {
      if (!tongue) continue;
      const flicker = still
        ? 0.75
        : 0.6 + Math.sin(time * (9 + i * 3.1) + i * 1.7) * 0.2 + Math.sin(time * (23 + i * 5) + i) * 0.15;
      const core = i === 0;
      const across = core ? 0 : (jitter(i, 1) - 0.5) * unit * 1.1;
      const deep = core ? 0 : (jitter(i, 2) - 0.5) * unit * 1.1;
      const tall = unit * (core ? 2.2 : 1.6 + jitter(i, 3) * 1.4) * (0.7 + flicker * 0.6);
      const wide = unit * (core ? 1.1 : 0.8 + jitter(i, 4) * 0.5);
      tongue.scale.set(wide, tall, 1);
      tongue.position.set(across, roof + tall * 0.42, deep);
      const material = tongue.material as { opacity: number };
      material.opacity = Math.min(1, 0.55 + flicker * 0.5);
    }

    for (const [i, ember] of embers.current.entries()) {
      if (!ember) continue;
      const phase = still ? (i + 0.5) / EMBERS : (time * (0.55 + jitter(i, 5) * 0.4) + jitter(i, 6)) % 1;
      ember.position.set(
        (jitter(i, 7) - 0.5) * unit * 2 + phase * unit * 0.8,
        roof + unit * 0.6 + phase * unit * 5,
        (jitter(i, 8) - 0.5) * unit * 2,
      );
      const size = unit * 0.28 * (1 - phase * 0.6);
      ember.scale.set(size, size, 1);
      const material = ember.material as { opacity: number };
      material.opacity = (1 - phase) * (0.6 + 0.4 * Math.sin(time * 17 + i * 3));
    }

    if (halo.current) {
      // The ground around a fire is lit by it, and the light breathes — the
      // one thing that still catches the eye when the flames themselves are
      // only a few pixels tall.
      const pulse = still ? 0.8 : 0.7 + Math.sin(time * 3.2) * 0.3;
      const size = unit * (plume.burning ? 7 : 3.5) * (0.85 + pulse * 0.25);
      halo.current.scale.set(size, size, 1);
      halo.current.position.y = roof * 0.5;
      const material = halo.current.material as { opacity: number };
      material.opacity = (plume.burning ? 0.85 : 0.35) * pulse;
    }

    if (!still) invalidate();
  });

  if (!plume) return null;

  return (
    <group>
      {plume.glowing && (
        <sprite ref={halo} renderOrder={1}>
          <spriteMaterial
            map={texture}
            color={plume.burning ? FLAME_DEEP : FLAME_MID}
            transparent
            opacity={0}
            depthWrite={false}
            blending={AdditiveBlending}
          />
        </sprite>
      )}

      {Array.from({ length: plume.count }, (_, i) => (
        <sprite
          key={`s${i}`}
          renderOrder={2}
          ref={(node) => {
            if (node) puffs.current[i] = node;
          }}
        >
          <spriteMaterial
            map={texture}
            color={plume.colour}
            transparent
            opacity={0}
            depthWrite={false}
            blending={NormalBlending}
          />
        </sprite>
      ))}

      {plume.burning &&
        Array.from({ length: FLAME_TONGUES }, (_, i) => (
          <sprite
            key={`f${i}`}
            renderOrder={3}
            ref={(node) => {
              if (node) flames.current[i] = node;
            }}
          >
            <spriteMaterial
              map={flame ?? texture}
              color={i === 0 ? FLAME_CORE : i % 2 ? FLAME_MID : FLAME_DEEP}
              transparent
              opacity={0}
              depthWrite={false}
              blending={AdditiveBlending}
            />
          </sprite>
        ))}

      {plume.glowing &&
        Array.from({ length: plume.burning ? EMBERS : 2 }, (_, i) => (
          <sprite
            key={`e${i}`}
            renderOrder={3}
            ref={(node) => {
              if (node) embers.current[i] = node;
            }}
          >
            <spriteMaterial
              map={texture}
              color={EMBER}
              transparent
              opacity={0}
              depthWrite={false}
              blending={AdditiveBlending}
            />
          </sprite>
        ))}
    </group>
  );
}
